import { EQUIPMENT_OPTIONS } from 'constants/equipment.constants'
import { stringToCurrency } from 'utils/finances.utils'
import Column from 'components/core/Column'
import Typography from 'components/core/Typography'
import Row from 'components/core/Row'
import { formatDateTimeRangeString } from 'utils/date.utils'
import { formatAddress } from 'utils/address.utils'
import { memo, useMemo } from 'react'
import { SERVICE_TYPE_OPTIONS } from 'constants/order.constants'
import { ACCESSORIAL_OPTIONS } from 'constants/accessorial.constants'
import type { ConfirmShipmentDetailsWidgetData } from 'types/widget.types'
import { getCommoditySizeString } from 'utils/commodity.utils'

export const ConfirmShipmentDetailsWidgetReadOnly = memo(
    ({ data }: { data: ConfirmShipmentDetailsWidgetData }) => {
        const {
            pickupLocation,
            pickupDate,
            deliveryLocation,
            deliveryDate,
            equipment,
            accessorials,
            maxRate,
            extraPickupLocations,
            extraDeliveryLocations,
            commodities,
            serviceType,
        } = useMemo(() => {
            const pickupLocationMutation = data.pickupLocation ?? {}
            const dropoffLocationMutation = data.dropoffLocation ?? {}
            const extraPickupLocationsMutations =
                data.extraPickupLocations ?? []
            const extraDropoffLocationsMutations =
                data.extraDropoffLocations ?? []
            const commoditiesMutations = data.commodities ?? []

            return {
                // Mimic the same behavior as the AddressInput.
                // Google shows no "name", address book entry has "name" - "address"
                pickupLocation: formatAddress(pickupLocationMutation, {
                    showName: !!pickupLocationMutation.addressBookId,
                }),
                extraPickupLocations: extraPickupLocationsMutations.map(
                    (extraPickup) =>
                        formatAddress(extraPickup, {
                            showName: !!extraPickup.addressBookId,
                        })
                ),
                pickupDate: formatDateTimeRangeString({
                    from: data.pickupDateTimeStartAt,
                    to: data.pickupDateTimeEndAt,
                    hasTime: false,
                }),
                deliveryLocation: formatAddress(dropoffLocationMutation, {
                    showName: !!dropoffLocationMutation.addressBookId,
                }),
                extraDeliveryLocations: extraDropoffLocationsMutations.map(
                    (extraDropoff) =>
                        formatAddress(extraDropoff, {
                            showName: !!extraDropoff.addressBookId,
                        })
                ),
                deliveryDate: formatDateTimeRangeString({
                    from: data.dropoffDateTimeStartAt,
                    to: data.dropoffDateTimeEndAt,
                    hasTime: false,
                }),
                equipment: data.equipment
                    ?.map(
                        (eq) =>
                            EQUIPMENT_OPTIONS.find(
                                (option) => option.value === eq
                            )?.label
                    )
                    .join(', '),
                accessorials: data.accessorials
                    ?.map(
                        (eq) =>
                            ACCESSORIAL_OPTIONS.find(
                                (option) => option.value === eq
                            )?.label
                    )
                    .join(', '),
                maxRate: stringToCurrency(data.maxRate ?? undefined),
                commodities: commoditiesMutations.map((commodity) => ({
                    quantityStr: [
                        commodity.freightType,
                        commodity.quantity ?? 1,
                    ]
                        .filter(Boolean)
                        .join(' x '),
                    descriptionStr: [
                        commodity.description ?? 'N/A',
                        commodity.numOfUnits,
                    ]
                        .filter(Boolean)
                        .join(' x '),
                    dimensionsStr: getCommoditySizeString(commodity, {
                        measurementType: data.measurementType,
                    }),
                    weightStr: [commodity.weight, data?.weightType ?? 'lbs']
                        .filter(Boolean)
                        .join(' '),
                })),
                serviceType:
                    SERVICE_TYPE_OPTIONS.find(
                        (option) => option.value === data.serviceType
                    )?.label ?? 'TL',
            }
        }, [data])

        return (
            <Column gap="p1" p="p2">
                {pickupLocation && (
                    <Column>
                        <Typography isBold variant="body2" color="primary">
                            Pickup location:
                        </Typography>
                        <Typography variant="body2" color="primary">
                            {pickupLocation}
                        </Typography>
                    </Column>
                )}
                {extraPickupLocations?.map((extraPickupLocation, index) => (
                    <Column key={index}>
                        <Typography isBold variant="body2" color="primary">
                            Pickup location {index + 2}:
                        </Typography>
                        <Typography variant="body2" color="primary">
                            {extraPickupLocation}
                        </Typography>
                    </Column>
                ))}
                {pickupDate && (
                    <Column>
                        <Typography isBold variant="body2" color="primary">
                            Pickup date:
                        </Typography>
                        <Typography variant="body2" color="primary">
                            {pickupDate}
                        </Typography>
                    </Column>
                )}
                {deliveryLocation && (
                    <Column>
                        <Typography isBold variant="body2" color="primary">
                            Delivery location:
                        </Typography>
                        <Typography variant="body2" color="primary">
                            {deliveryLocation}
                        </Typography>
                    </Column>
                )}
                {extraDeliveryLocations?.map((extraDeliveryLocation, index) => (
                    <Column key={index}>
                        <Typography isBold variant="body2" color="primary">
                            Delivery location {index + 2}:
                        </Typography>
                        <Typography variant="body2" color="primary">
                            {extraDeliveryLocation}
                        </Typography>
                    </Column>
                ))}
                {deliveryDate && (
                    <Column>
                        <Typography isBold variant="body2" color="primary">
                            Delivery date:
                        </Typography>
                        <Typography variant="body2" color="primary">
                            {deliveryDate}
                        </Typography>
                    </Column>
                )}
                {data.referenceId && (
                    <Column>
                        <Typography isBold variant="body2" color="primary">
                            Reference ID:
                        </Typography>
                        <Typography variant="body2" color="primary">
                            {data.referenceId}
                        </Typography>
                    </Column>
                )}
                {maxRate && (
                    <Column>
                        <Typography isBold variant="body2" color="primary">
                            Budget:
                        </Typography>
                        <Typography variant="body2" color="primary">
                            {maxRate}
                        </Typography>
                    </Column>
                )}
                {equipment && (
                    <Column>
                        <Typography isBold variant="body2" color="primary">
                            Equipment:
                        </Typography>
                        <Typography variant="body2" color="primary">
                            {data.equipment
                                ?.map(
                                    (eq) =>
                                        EQUIPMENT_OPTIONS.find(
                                            (option) => option.value === eq
                                        )?.label
                                )
                                .join(', ') || '--'}
                        </Typography>
                    </Column>
                )}
                {accessorials && (
                    <Column>
                        <Typography isBold variant="body2" color="primary">
                            Accessorials:
                        </Typography>
                        <Typography variant="body2" color="primary">
                            {data.accessorials
                                ?.map(
                                    (accessorial) =>
                                        ACCESSORIAL_OPTIONS.find(
                                            (option) =>
                                                option.value === accessorial
                                        )?.label
                                )
                                .join(', ') || '--'}
                        </Typography>
                    </Column>
                )}
                {data.specialInstructions && (
                    <Column>
                        <Typography isBold variant="body2" color="primary">
                            Special instructions:
                        </Typography>
                        <Typography variant="body2" color="primary">
                            {data.specialInstructions}
                        </Typography>
                    </Column>
                )}
                {serviceType && (
                    <Column>
                        <Typography isBold variant="body2" color="primary">
                            Service type:
                        </Typography>
                        <Typography variant="body2" color="primary">
                            {serviceType}
                        </Typography>
                    </Column>
                )}
                {data.isApprovalRequired && (
                    <Column>
                        <Typography isBold variant="body2" color="primary">
                            Approval required:
                        </Typography>
                        <Typography variant="body2" color="primary">
                            {data.isApprovalRequired ? 'Yes' : 'No'}
                        </Typography>
                    </Column>
                )}
                {data.isRushedOrder && (
                    <Column>
                        <Typography isBold variant="body2" color="primary">
                            Rush order:
                        </Typography>
                        <Typography variant="body2" color="primary">
                            {data.isRushedOrder ? 'Yes' : 'No'}
                        </Typography>
                    </Column>
                )}
                {!!data.numberOfShipments && data.numberOfShipments > 1 && (
                    <Column>
                        <Typography isBold variant="body2" color="primary">
                            Quantity:
                        </Typography>
                        <Typography variant="body2" color="primary">
                            {data.numberOfShipments}
                        </Typography>
                    </Column>
                )}
                {!!commodities.length && (
                    <Column>
                        <Typography isBold variant="body2" color="primary">
                            Commodities:
                        </Typography>
                        {commodities.map((c, index) => (
                            <Column key={index}>
                                <Typography variant="body2" color="primary">
                                    -{' '}
                                    {[
                                        c.descriptionStr,
                                        c.quantityStr,
                                        c.dimensionsStr,
                                        c.weightStr,
                                    ].join(', ')}
                                </Typography>
                            </Column>
                        ))}
                    </Column>
                )}
                <Row justifyContent="flex-end">
                    <Typography isItalic variant="body2" color="secondary">
                        {data.status === 'confirmed'
                            ? 'Confirmed'
                            : 'Cancelled'}
                    </Typography>
                </Row>
            </Column>
        )
    }
)
